<template>
    <q-dialog
        full-width
        full-height
        ref="workOrderDialogRef"
        @hide="onDialogHide"
        v-model="workOrderDialogStore.open"
        :maximized="$q.screen.lt.md"
        :no-focus="(minimized || docked)"
        :seamless="(minimized || docked)"
        :position="(minimized || docked) ? 'bottom' : undefined"
    >
        <q-card
            :flat="$q.dark.isActive"
            :square="$q.dark.isActive"
            :bordered="$q.dark.isActive"
            class="q-dialog-plugin relative-position"
        >
            <q-toolbar
                :class="{
                    'bg-primary text-white': !$q.dark.isActive,
                    'cursor-pointer': minimized,
                }"
                @click="minimized ? (minimized = false) : undefined"
                :style="($q.dark.isActive && (minimized || docked)) ? `background-color: #0f0f0f !important;` : ''"
            >
                <q-toolbar-title :class="{'text-subtitle1': $q.screen.lt.md}">
                    <span v-if="workOrderId < 1">Creating A New Work Order For </span>
                    <span 
                        v-if="workOrder.contact?.org_name ?? false" 
                        :class="{'text-secondary':$q.dark.isActive}"
                    >
                        {{ workOrder.contact.org_name ?? '*loading...*' }}
                    </span>&nbsp;<span v-if="workOrderId > 0">Work Order</span>
                    <span :class="{'text-secondary':$q.dark.isActive}" v-if="workOrderId > 0">
                        {{ workOrderId ? ' #' + workOrderId : '' }}
                    </span>
                </q-toolbar-title>

                <q-btn
                    flat
                    padding="3px"
                    :icon="'mdi-' + (!minimized ? 'window-minimize' : 'chevron-up')"
                    @click.stop="minimized = !minimized"
                >
                    <q-tooltip
                        anchor="top middle"
                        self="bottom middle"
                        :offset="[0, 10]"
                    >
                        {{ minimized ? 'Restore' : 'Minimize' }}
                    </q-tooltip>
                </q-btn>

                <q-btn
                    flat
                    padding="3px"
                    :icon="'mdi-window-' + (docked ? 'maximize' : 'restore')"
                    @click.stop="docked = !docked"
                    v-if="$q.screen.gt.sm"
                />

                <q-btn
                    flat
                    padding="3px"
                    icon="mdi-close"
                    @click.stop="workOrderDialogStore.closeDialog()"
                />
            </q-toolbar>

            <q-separator v-if="!minimized" />

            <q-card-section v-if="!minimized" style="height: calc(100% - 97px); position: relative;">
                <div class="row full-height" :style="$q.screen.lt.md ? 'flex-direction: column;' : ''">
                    <div class="col-12 col-md-6 col-lg-4" :style="$q.screen.lt.md ? 'flex: 0;' : ''">
                        <WorkOrderDetails 
                            v-model:work-order="workOrderDialogStore.workOrder"
                            v-model:is-locked="workOrderDialogStore.locked"
                            :editable="workOrderDialogStore.workOrder.status !== 'invoiced'"
                            :editing="false"
                            :is-modified="false"
                            :is-new="workOrderDialogStore.workOrder.id === null"
                            :new-work-items-length="0"
                            :work-items-length="workOrderDialogStore.workItems.rows.length"
                            :loading="workOrderDialogStore.workOrder.loading"
                        />
                        <!-- Search/filter for work items -->
                        <q-card 
                            flat 
                            square 
                            bordered
                            class="q-mt-sm"
                            :style="$q.dark.isActive ? `background-color: #0f0f0f !important;` : ''"
                        >
                            <q-card-section class="q-pa-none">
                                <q-expansion-item
                                    expand-icon-toggle
                                    expand-icon="mdi-filter-menu"
                                    expanded-icon="mdi-filter-minus"
                                    :header-style="$q.dark.isActive ? `background-color: transparent !important;` : ''"
                                    v-if="workOrder.id"
                                >
                                    <template #header>
                                        <q-item-section>
                                            <q-input
                                                v-model="workItemFilter.search"
                                                square
                                                outlined
                                                dense
                                                placeholder="Search..."
                                                :label="`Search ${workItemFilter.searchColumn.length > 0 ? 'selected' : 'all'} column${workItemFilter.searchColumn.length != 1 ? 's' : ''}`"
                                            >
                                                <template #after>
                                                    <q-btn
                                                        flat
                                                        dense
                                                        icon="mdi-magnify"
                                                        padding="8.5px"
                                                        @click="workOrderDialogStore.getWorkItems(null, true)"
                                                        :disable="loading.workItems"
                                                    >
                                                        <q-inner-loading :showing="loading.workItems">
                                                            <q-spinner size="1.5rem" />
                                                        </q-inner-loading>
                                                    </q-btn>
                                                </template>
                                            </q-input>
                                        </q-item-section>
                                        <q-item-section style="padding-left: 5px;" side>
                                            <q-btn
                                                flat
                                                @click="workOrderDialogStore.fetch(null, null, true)"
                                                :disable="loading.workItems"
                                                :color="loading.workItems ? 'secondary' : undefined"
                                                padding="8.5px"
                                            >
                                                <q-icon name="mdi-refresh" :class="{spin: loading.workItems}" />
                                            </q-btn>
                                        </q-item-section> 
                                        <q-item-section v-if="$q.screen.lt.md" style="padding-left: 5px;" side>
                                            <q-btn
                                                flat
                                                :disable="loading.workItems"
                                                :color="$q.dark.isActive ? 'green-13' : 'green'"
                                                icon="mdi-playlist-plus"
                                                padding="8.5px"
                                                @click="workOrderDialogStore.create()"
                                            />
                                        </q-item-section>
                                    </template>

                                    <div :class="{'q-px-md row': 1, 'q-pb-sm': $q.screen.lt.md}">
                                        <div class="col-12 col-md-6 col-lg-8">
                                            <q-select
                                                v-model="workItemFilter.searchColumn"
                                                square
                                                use-chips
                                                stack-label
                                                :class="{
                                                    'q-mr-sm': $q.screen.gt.sm,
                                                    'q-mb-sm': $q.screen.lt.md
                                                }"
                                                outlined
                                                dense
                                                multiple
                                                emit-value
                                                map-options
                                                :options="workItemColumns"
                                                placeholder="Search Columns..."
                                                label="Search Columns"
                                                :disable="loading.workItems"
                                            />
                                        </div>

                                        <div class="col-12 col-md-6 col-lg-4">
                                            <q-select
                                                square
                                                outlined
                                                dense
                                                emit-value
                                                map-options
                                                :options="[{label: 'Created At',value: 'created_at'},{label: 'Updated At',value: 'updated_at'}]"
                                                placeholder="Sort By..."
                                                label="Sort By Column"
                                                v-model="workItemFilter.sort"
                                                :disable="loading.workItems"
                                            >
                                                <template #after>
                                                    <q-btn
                                                        flat
                                                        padding="8.5px"
                                                        :icon="'mdi-sort-' + (workItemFilter.direction === 'asc' ? 'ascending' : 'descending')"
                                                        @click.prevent="workItemFilter.direction = workItemFilter.direction === 'asc' ? 'desc' : 'asc'"
                                                    >
                                                        <q-tooltip>
                                                            {{ workItemFilter.direction === 'asc' ? 'Descending' : 'Ascending' }}
                                                        </q-tooltip>
                                                    </q-btn>
                                                </template>
                                            </q-select>
                                        </div>

                                        <div class="col-12 q-mt-sm">
                                            <q-select
                                                dense
                                                square
                                                outlined
                                                emit-value
                                                map-options
                                                v-model="workItemFilter.rows"
                                                @update:model-value="workOrderDialogStore.fetchItems()"
                                                :options="rows"
                                                label="Rows Per Page"
                                            />
                                        </div>
                                    </div>
                                </q-expansion-item>
                            </q-card-section>

                            <q-item v-if="$q.screen.gt.sm" :class="{'q-py-none': workOrder.id}">
                                <q-item-section>
                                    <q-item-label v-if="workItemFilter.total > 0" caption>
                                        <!-- {{ workItemFilterOptions }} -->
                                        <!-- Showing n of n work items -->
                                    </q-item-label>
                                </q-item-section>
                                <q-item-section side>
                                    <q-btn
                                        v-if="((editing || locked) || workOrder.status !== 'invoiced')"
                                        icon="mdi-playlist-plus"
                                        :color="$q.dark.isActive ? 'green-13' : 'green'"
                                        label="Add New Work Item"
                                        size="sm"
                                        @click="workOrderDialogStore.create()"
                                        :outline="$q.dark.isActive"
                                    >
                                        <q-tooltip>
                                            Add New Work Item
                                        </q-tooltip>
                                    </q-btn>
                                </q-item-section>
                            </q-item>
                        </q-card>
                    </div>
                    
                    <div :style="$q.screen.lt.md ? 'flex: 1;' : ''" :class="{
                        'col-12 col-md-6 col-lg-8': 1,
                    }">
                        <q-scroll-area
                            :visible="true"
                            style="height: calc(100%);"
                            :class="{
                                'q-pt-sm': $q.screen.lt.md,
                                'q-px-md': $q.screen.gt.sm,
                            }"
                        >
                            <!-- TODO: Fix this before release -->
                            <!-- <q-card
                                flat
                                square
                                bordered
                                v-for="(workItem, index) in workItemsNew"
                                :key="`${index}-${workItem.id}`"
                                class="q-mb-sm"
                            >
                                <EditWorkItem
                                    v-model:model-value="workItemsNew[index]"
                                    :customer="{ id: customerId, ...workTypes}"
                                    :disable-create="!workOrder.id"
                                    @save="$event => storeWorkItem($event, true, index)"
                                    @cancel="cancelNewWorkItem(index)"
                                    @save-all="submitWorkOrder"
                                    @delete="deleteWorkItem"
                                    :identifier-types="identifierTypes"
                                    :work-start-at="workOrder.work_start_at"
                                    :error="newWorkItemErrors[index] ?? undefined"
                                />
                            </q-card> -->

                            <q-card
                                flat
                                square
                                bordered
                                v-for="(workItem, index) in workItems"
                                :key="`${index}-${workItem.id}`"
                                class="q-mb-sm"
                            >
                                <!-- <EditWorkItem
                                    v-if="editing"
                                    :model-value="workItemUpdates.find(v => v.id == workItem.id) ?? workItem"
                                    @update:model-value="$event => updateWorkItem($event)"
                                    @cancel="cancelWorkItem"
                                    @save="$event => storeWorkItem($event, false, index)"
                                    :customer="{ id: customerId, ...workTypes}"
                                    :disable-create="!workOrder.id"
                                    @save-all="submitWorkOrder"
                                    @delete="deleteWorkItem"
                                    :identifier-types="identifierTypes"
                                    :work-start-at="workOrder.work_start_at"
                                    :error="workItemErrors.find(v => v.index === index && v.id === workItem.id) ?? undefined"
                                /> -->

                                <WorkItemView
                                
                                    @update:model-value="$event => workOrderDialogStore.update($event, false, index)"
                                    :customer="workOrderDialogStore.customer"
                                    :model-value="workItem"
                                    :enable-crud="workOrder.status !== 'invoiced'"
                                    :locked="false"
                                    @delete="workOrderDialogStore.destroy(workItem.id)"
                                    :work-start-at="workOrder.work_start_at"
                                />
                            </q-card>

                            <q-inner-loading :showing="workItems.length === 0 && workItemsEditing.length === 0">
                                <q-item-label class="text-h6 text-grey-5" v-if="!loading.workItems">
                                    No Work Items Found
                                </q-item-label>

                                <q-item-label class="text-caption text-info" v-if="workItemFilter.isDirty">
                                    Try changing your search criteria
                                </q-item-label>
                                <q-item-label v-else-if="!loading.workItems">
                                    <q-btn 
                                        size="sm" 
                                        icon="mdi-playlist-plus"
                                        :color="$q.dark.isActive ? 'green-13' : 'green'"
                                        @click="workOrderDialogStore.create()"
                                        label="Add work item"
                                    />
                                </q-item-label>
                            </q-inner-loading>

                            <q-inner-loading :showing="loading.workItems">
                                <q-spinner size="1.5rem" />
                                Loading Work Items
                            </q-inner-loading>
                        </q-scroll-area>

                        <q-item v-if="workOrderDialogStore.workItems.filter.total > 0">
                            <q-item-section :side="$q.screen.lt.md">
                                <q-btn
                                    square no-caps
                                    :color="workOrderDialogStore.workItems.filter.from === 1 ? 'grey-8' : 'primary'"
                                    :label="$q.screen.gt.sm ? 'Previous Page' : undefined"
                                    :icon="$q.screen.lt.md ? 'mdi-chevron-left' : undefined"


                                    :disable="workOrderDialogStore.workItems.filter.from === 1"
                                    @click="workOrderDialogStore.workItems.filter.form.page--"
                                />
                            </q-item-section>
                            <q-item-section class="text-center">
                                <q-item-label>
                                    Showing {{ workOrderDialogStore.workItems.filter.current_page }} of {{ workOrderDialogStore.workItems.filter.last_page }} page{{ workOrderDialogStore.workItems.filter.last_page != 1 ? 's' : '' }}
                                </q-item-label>
                                <q-item-label caption>
                                    Or {{ workOrderDialogStore.workItems.filter.from }}-{{ workOrderDialogStore.workItems.filter.total }} of {{ workOrderDialogStore.workItems.filter.to }} work items
                                </q-item-label>
                                <!-- Popup and helper tooltip should show only when useful -->
                                <template v-if="workOrderDialogStore.workItems.filter.last_page > 1">
                                    <q-tooltip>
                                        <div class="text-bold">
                                            Click to change page manually
                                        </div>
                                        Showing {{ workOrderDialogStore.workItems.filter.current_page }} of {{ workOrderDialogStore.workItems.filter.last_page }} page{{ workOrderDialogStore.workItems.filter.last_page != 1 ? 's' : '' }}
                                    </q-tooltip>

                                    <q-popup-edit 
                                        auto-save 
                                        v-slot="scope"
                                        :model-value="workOrderDialogStore.workItems.filter.form.page"
                                    >
                                        <q-input 
                                            dense
                                            autofocus
                                            type="number"
                                            inputmode="numeric"
                                            :rules="[
                                                val => val > 0 || 'Page must be greater than 0',
                                                val => val <= workOrderDialogStore.workItems.filter.last_page || `Page must be less than ${workOrderDialogStore.workItems.filter.last_page + 1}`
                                            ]"
                                            label="Page"
                                            v-model="scope.value"
                                            @keyup.enter="scope.set" 
                                        />
                                    </q-popup-edit>
                                </template>
                            </q-item-section>
                            <q-item-section :side="$q.screen.lt.md">
                                <q-btn
                                    square no-caps
                                    :color="(workOrderDialogStore.workItems.filter.last_page !== workItemFilter.page || workOrderDialogStore.workItems.filter.last_page < 2) ? 'grey-8' : 'primary'"
                                    :label="$q.screen.gt.sm ? 'Next Page' : undefined"
                                    :icon="$q.screen.lt.md ? 'mdi-chevron-right' : undefined"

                                    :disable="workOrderDialogStore.workItems.filter.last_page !== workItemFilter.page || workOrderDialogStore.workItems.filter.last_page < 2"
                                    @click="setPage(workItemFilter.page++)"
                                />
                            </q-item-section>
                        </q-item>
                    </div>
                </div>
            </q-card-section>
        </q-card>
    </q-dialog>
</template>

<script>
import { useDialogPluginComponent } from 'quasar';

import WorkItemView from './Components/WorkItem/WorkItemView.vue';
import WorkItemEdit from './Components/WorkItem/WorkItemEdit.vue';

import SwapDialog from './Components/SwapDialog/SwapDialog.vue';

import WorkOrderDetails from './Components/Details.vue';

/** Soon to take over the older WorkOrderDialog solution */
export default {
    name: "WorkOrderDialog",
    emits: [...useDialogPluginComponent.emits],
    components: {
        WorkItemView,
        WorkItemEdit,
        SwapDialog,
        WorkOrderDetails,
    },
    setup () {
        const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent()

        return {
            dialogRef,
            onDialogHide,
            onCancelClick: onDialogCancel,
        }
    },
    data() {
        return {
            workItemColumns: [
                {
                    label: 'Notes',
                    value: "notes"
                },
                {
                    label: 'Work Type',
                    value: "work_type"
                },
                {
                    label: 'Sub-Customer',
                    value: "sub_customer"
                },
                {
                    label: 'Customer',
                    value: "customer"
                },
                {
                    label: 'Worker',
                    value: "lumper"
                },
                {
                    label: 'Created At',
                    value: "created_at"
                },
                {
                    label: 'Updated At',
                    value: "updated_at"
                },
            ],
            rows: [
                {
                    label: '5',
                    value: 5,
                },
                {
                    label: '7',
                    value: 7,
                },
                {
                    label: '10',
                    value: 10,
                },
                {
                    label: '15',
                    value: 15,
                },
                {
                    label: '25',
                    value: 25,
                },
                {
                    label: '50',
                    value: 50,
                },
                {
                    label: 'All',
                    value: 0,
                },
            ],
        };
    },
    computed: {
        workOrderId() {
            return this.workOrderDialogStore.workOrder.id;
        },
        customerId() {
            return this.workOrderDialogStore.customerId;
        },
        subCustomerId() {
            return this.workOrderDialogStore.subCustomerId;
        },
        minimized: {
            get() {
                return this.workOrderDialogStore.minimized;
            },
            set(value) {
                this.workOrderDialogStore.minimized = value;
            }
        },
        docked: {
            get() {
                return this.workOrderDialogStore.docked;
            },
            set(value) {
                if (this.minimized) this.minimized = false;

                this.workOrderDialogStore.docked = value;
            }
        },
        editing: {
            get() {
                return this.workOrderDialogStore.editing;
            },
            set(value) {
                this.workOrderDialogStore.editing = value;
            }
        },
        workOrder() {
            return this.workOrderDialogStore.workOrder;
        },
        workItems() {
            return this.workOrderDialogStore.workItems.rows;
        },
        workItemsEditing() {
            return this.workOrderDialogStore.workItems.editing;
        },
        workItemFilter() {
            return this.workOrderDialogStore.workItems.filter.form;
        },
        loading() {
            return this.workOrderDialogStore.loading;
        },
    },
    watch: {
        'workOrderDialogStore.open'(to) {
            if (to) return;
            
            this.workOrderDialogStore.reset();
        },
    }
}
</script>